<template>
  <div class="user-activity-chart">
    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center p-8">
      <ProgressSpinner size="medium" />
      <span class="ml-3 text-gray-600">{{ $t('UserActivityChartComponent.loading') }}</span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="flex flex-col items-center p-8 text-center">
      <i class="pi pi-exclamation-triangle text-red-500 text-4xl mb-3"></i>
      <p class="text-red-600 mb-3">{{ error }}</p>
      <Button
        :label="$t('UserActivityChartComponent.retry')"
        icon="pi pi-refresh"
        @click="loadChartData"
        severity="secondary"
        outlined
      />
    </div>

    <!-- Chart Content -->
    <div v-else class="chart-container">
      <!-- Chart Header -->
      <div class="flex justify-between items-center mb-4">
        <div>
          <p class="text-sm text-gray-600">
            {{ getPeriodLabel(periodType) }} • {{ formatDateRange(periodType) }}
          </p>
        </div>

        <!-- Period Selector -->
        <div class="flex flex-col gap-3">
          <div class="flex items-center gap-2">
            <label class="text-sm font-medium text-gray-700">
              {{ $t('UserActivityChartComponent.period') }}
            </label>
            <Dropdown
              v-model="selectedPeriod"
              :options="periodOptions"
              option-label="label"
              option-value="value"
              @change="onPeriodChange"
              class="w-40"
            />
          </div>

          <!-- Custom Date Range Picker (shown when custom is selected) -->
          <div v-if="selectedPeriod === 'custom'"
               class="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div class="flex items-center gap-2">
              <label class="text-sm font-medium text-gray-700 whitespace-nowrap">
                {{ $t('UserActivityChartComponent.fromDate') }}
              </label>
              <Calendar
                v-model="customStartDate"
                dateFormat="yy-mm-dd"
                :placeholder="$t('UserActivityChartComponent.selectStartDate')"
                class="w-48"
                @update:modelValue="onCustomDateChange"
                showIcon
                iconDisplay="input"
              />
            </div>
            <div class="flex items-center gap-2">
              <label class="text-sm font-medium text-gray-700 whitespace-nowrap">
                {{ $t('UserActivityChartComponent.toDate') }}
              </label>
              <Calendar
                v-model="customEndDate"
                dateFormat="yy-mm-dd"
                :placeholder="$t('UserActivityChartComponent.selectEndDate')"
                class="w-48"
                @update:modelValue="onCustomDateChange"
                showIcon
                iconDisplay="input"
                :minDate="customStartDate"
              />
            </div>
            <Button
              v-if="customStartDate && customEndDate"
              icon="pi pi-times"
              :label="$t('UserActivityChartComponent.clear')"
              class="p-button-text p-button-sm"
              @click="clearCustomDateRange"
              severity="secondary"
            />
          </div>
        </div>
      </div>

      <!-- Activity Summary Cards -->
      <!-- For users without time data: show only 2 cards (Login Events + Activity Level) -->
      <!-- For users with time data: show 3 cards (Total Time + Active Days + Activity Level) -->
      <div :class="hasNoTimeData ? 'grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'
       : 'grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'">
        <div class="bg-white rounded-lg p-4 shadow-sm border">
          <div class="flex items-center">
            <div class="p-2 rounded-full" :class="hasNoTimeData ? 'bg-purple-100' : 'bg-blue-100'">
              <i class="pi" :class="hasNoTimeData ? 'pi-sign-in text-purple-600'
                : 'pi-clock text-blue-600'"></i>
            </div>
            <div class="ml-3">
              <p class="text-sm text-gray-600">
                {{ hasNoTimeData
                  ? (($t('UserActivityChartComponent.summaryCards.loginEvents')
                    !== 'UserActivityChartComponent.summaryCards.loginEvents'
                       ? $t('UserActivityChartComponent.summaryCards.loginEvents')
                       : 'Login Events'))
                    : $t('UserActivityChartComponent.summaryCards.totalTime') }}
              </p>
              <p class="text-lg font-semibold text-gray-800">
                {{ activitySummary.formattedTotal }}
              </p>
            </div>
          </div>
        </div>

        <!-- KPI Records / Active Days card - only show for users with time data -->
        <div v-if="!hasNoTimeData" class="bg-white rounded-lg p-4 shadow-sm border">
          <div class="flex items-center">
            <div class="p-2 rounded-full bg-green-100">
              <i class="pi pi-calendar text-green-600"></i>
            </div>
            <div class="ml-3">
              <p class="text-sm text-gray-600">
                {{ $t('UserActivityChartComponent.summaryCards.activeDays') }}
              </p>
              <p class="text-lg font-semibold text-gray-800">
                {{ activitySummary.activeDays }}
              </p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg p-4 shadow-sm border">
          <div class="flex items-center">
            <div
              class="p-2 rounded-full"
              :style="{ backgroundColor: activitySummary.activityLevel.color + '20' }"
            >
              <i
                class="pi pi-chart-line"
                :style="{ color: activitySummary.activityLevel.color }"
              ></i>
            </div>
            <div class="ml-3">
              <p class="text-sm text-gray-600">
                {{ $t('UserActivityChartComponent.summaryCards.activityLevel') }}</p>
              <p
                class="text-lg font-semibold"
                :style="{ color: activitySummary.activityLevel.color }"
              >
                {{ activitySummary.activityLevel.label }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Activity Breakdown (shows why the user is active/inactive) -->
      <div v-if="activityBreakdown" class="mb-6">
        <p class="text-sm font-medium text-gray-700 mb-2">
          {{ ($t('UserActivityChartComponent.activityBreakdown.title')
              !== 'UserActivityChartComponent.activityBreakdown.title'
              ? $t('UserActivityChartComponent.activityBreakdown.title')
              : 'Activity Breakdown') }}
          <span class="text-xs font-normal text-gray-500 ml-1">
            ({{ activityBreakdownPeriodLabel }})
          </span>
        </p>
        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          <div v-for="item in activityBreakdown" :key="item.key"
               class="flex items-center gap-2 px-3 py-2 rounded-lg border"
               :class="item.count > 0 ? 'bg-green-50 border-green-200' :
                'bg-gray-50 border-gray-200'">
            <i class="pi text-sm"
               :class="[item.icon, item.count > 0 ? 'text-green-600' : 'text-gray-400']"></i>
            <div class="min-w-0">
              <p class="text-xs text-gray-500 truncate">{{ item.label }}</p>
              <p class="text-sm font-semibold"
                 :class="item.count > 0 ? 'text-green-700' : 'text-gray-400'">
                {{ item.count }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Technical Hint Message -->
      <div v-if="hasNoTimeData" class="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div class="flex items-start">
          <i class="pi pi-info-circle text-amber-600 mt-1 mr-3"></i>
          <div>
            <p class="text-sm font-medium text-amber-800 mb-1">
              {{ ($t('UserActivityChartComponent.noTimeData.title')
               !== 'UserActivityChartComponent.noTimeData.title'
                  ? $t('UserActivityChartComponent.noTimeData.title')
                  : 'Time Calculations Unavailable') }}
            </p>
            <p class="text-xs text-amber-700">
              {{ ($t('UserActivityChartComponent.noTimeData.description')
               !== 'UserActivityChartComponent.noTimeData.description'
                  ? $t('UserActivityChartComponent.noTimeData.description')
                  : 'For technical reasons, time-based calculations are not\
                   available for this user. However, login activity is being\
                    tracked and displayed above.') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Chart (only show after DOM is ready, with valid data
        — prevents Chart.js null-canvas crash) -->
      <div v-if="!hasNoTimeData && hasValidChartData && chartReady"
       class="bg-white rounded-lg p-6 shadow-sm border">
        <Chart
          :key="chartRenderKey"
          type="bar"
          :data="chartData"
          :options="chartOptions"
          class="w-full"
          style="height: 300px"
        />
      </div>

      <!-- No Data State -->
      <div
        v-else-if="!hasNoTimeData && !hasValidChartData && !loading && !error"
        class="flex flex-col items-center p-8 text-center"
      >
        <i class="pi pi-chart-bar text-gray-400 text-4xl mb-3"></i>
        <p class="text-gray-600 mb-2">{{ $t('UserActivityChartComponent.noData.title',
         { period: getPeriodLabel(periodType) }) }}</p>
        <p class="text-sm text-gray-500">
          {{ $t('UserActivityChartComponent.noData.description') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'primevue/chart';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import ProgressSpinner from 'primevue/progressspinner';
import Calendar from 'primevue/calendar';
import { mapGetters } from 'vuex';
import {
  extractChartData,
  getActivitySummary,
  getActivityLevelFromCounts,
  getPeriodLabel,
} from '@/helpers/timeKpiHelper';

export default {
  name: 'UserActivityChartComponent',

  components: {
    Chart,
    Button,
    Dropdown,
    ProgressSpinner,
    Calendar,
  },

  props: {
    userId: {
      type: String,
      required: true,
    },
    userName: {
      type: String,
      required: true,
    },
    initialPeriodType: {
      type: String,
      default: 'weekly',
      validator: (value) => ['daily', 'weekly', 'monthly', 'custom'].includes(value),
    },
    startDate: {
      type: Date,
      default: null,
    },
    endDate: {
      type: Date,
      default: null,
    },
  },

  data() {
    return {
      selectedPeriod: this.initialPeriodType,
      customStartDate: null,
      customEndDate: null,
      chartReady: false, // Flipped to true after DOM is painted,
      //  prevents Chart.js null-canvas crash
    };
  },

  computed: {
    ...mapGetters('userTimeKpi', ['getUserActivityData', 'isLoading', 'getError']),

    periodType() {
      return this.selectedPeriod;
    },

    periodOptions() {
      return [
        { label: this.$t('UserActivityChartComponent.periodOptions.last24Hours'), value: 'daily' },
        { label: this.$t('UserActivityChartComponent.periodOptions.lastWeek'), value: 'weekly' },
        { label: this.$t('UserActivityChartComponent.periodOptions.lastMonth'), value: 'monthly' },
        { label: this.$t('UserActivityChartComponent.periodOptions.custom'), value: 'custom' },
      ];
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top',
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const value = context.parsed.y;
                return this.$t('UserActivityChartComponent.chart.tooltip', {
                  label: context.dataset.label,
                  value,
                });
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: this.$t('UserActivityChartComponent.chart.hours'),
            },
          },
          x: {
            title: {
              display: true,
              text: this.$t('UserActivityChartComponent.chart.date'),
            },
          },
        },
      };
    },

    loading() {
      return this.isLoading('activity') || this.isLoading('chart');
    },

    error() {
      return this.getError('activity') || this.getError('chart');
    },

    /**
     * Whether chartData contains actual renderable data (not empty/placeholder).
     * Prevents Chart.js from initializing with a null canvas or empty data.
     */
    hasValidChartData() {
      const cd = this.chartData;
      if (!cd || !cd.labels || !cd.datasets) return false;
      if (cd.labels.length === 0) return false;
      if (cd.labels.length === 1 && cd.labels[0] === 'No Data') return false;
      return cd.datasets.length > 0;
    },

    /**
     * Unique key that changes when chart data changes, forcing PrimeVue Chart
     * to cleanly destroy and recreate its internal Chart.js instance.
     */
    chartRenderKey() {
      return `chart_${this.userId}_${this.periodType}_${this.chartData?.labels?.length || 0}`;
    },

    chartData() {
      // Build the chart key for custom date ranges
      let chartKey = this.periodType;

      if (this.periodType === 'custom' && this.customStartDate && this.customEndDate) {
        const startDate = new Date(this.customStartDate);
        const endDate = new Date(this.customEndDate);
        // Build the key in the same format as the store uses
        chartKey = `${this.userId}_custom_${startDate.toISOString()}_${endDate.toISOString()}`;
      } else {
        // For regular periods, build the key with userId
        chartKey = `${this.userId}_${this.periodType}`;
      }

      // Try to get period-specific chart data first, fallback to general activity data
      const periodData = this.$store.getters['userTimeKpi/getUserChartData'](this.userId, chartKey);
      const generalData = this.getUserActivityData(this.userId);

      const data = periodData || generalData;

      if (!data) {
        return { labels: [], datasets: [] };
      }

      const chartData = extractChartData(data);
      return chartData;
    },

    activitySummary() {
      // Build the chart key for custom date ranges
      let chartKey = this.periodType;

      if (this.periodType === 'custom' && this.customStartDate && this.customEndDate) {
        const startDate = new Date(this.customStartDate);
        const endDate = new Date(this.customEndDate);
        // Build the key in the same format as the store uses
        chartKey = `${this.userId}_custom_${startDate.toISOString()}_${endDate.toISOString()}`;
      } else {
        // For regular periods, build the key with userId
        chartKey = `${this.userId}_${this.periodType}`;
      }

      // Try to get period-specific chart data first, fallback to general activity data
      const periodData = this.$store.getters['userTimeKpi/getUserChartData'](this.userId, chartKey);
      const generalData = this.getUserActivityData(this.userId);

      // Use period data if it exists, otherwise use general data
      const data = periodData || generalData;

      // Check if we have login events but no metrics (technical issue)
      // IMPORTANT: Only show login events view if there are NO metrics at all
      const hasLoginEvents = data?.stored?.loginEventsCount || data?.loginEventsCount;
      const hasMetrics = data?.stored?.metrics || data?.metrics
        || data?.calculation?.timeMetrics || data?.calculation?.metrics
        || (data?.sessionMetrics?.totalTimeSpent > 0);

      // Also check general data for login events if current data doesn't have them
      const generalHasLoginEvents = generalData?.stored?.loginEventsCount
       || generalData?.loginEventsCount;
      const generalHasMetrics = generalData?.stored?.metrics || generalData?.metrics
        || (generalData?.sessionMetrics?.totalTimeSpent > 0);

      // If current data has no metrics but general data has
      // login events (and no metrics), use general data
      const finalHasLoginEvents = hasLoginEvents
       || (!hasMetrics && generalHasLoginEvents && !generalHasMetrics);
      const finalHasMetrics = hasMetrics || generalHasMetrics;

      // If no metrics but we have login events, return a special summary
      // IMPORTANT: Only do this if there are NO metrics at all (user has no time data)
      if (!finalHasMetrics && finalHasLoginEvents) {
        // Use general data for login events count if current data doesn't have it
        const dataToUse = (!hasLoginEvents && generalHasLoginEvents) ? generalData : data;
        const loginEventsCountValue = dataToUse?.stored?.loginEventsCount
         || dataToUse?.loginEventsCount || 0;
        const kpiRecordsCountValue = dataToUse?.stored?.kpiRecordsCount
         || dataToUse?.kpiRecordsCount || 0;

        // Helper to get translation with fallback
        const t = (key, fallback) => {
          const translated = this.$t(key);
          return translated !== key ? translated : fallback;
        };

        // Use unified event-count-based activity level (same as user table)
        const noTimeTranslations = {
          veryActive: t('UserAdministrationComponent.activityLevels.veryActive', 'Very Active'),
          active: t('UserAdministrationComponent.activityLevels.active', 'Active'),
          moderate: t('UserAdministrationComponent.activityLevels.moderate', 'Moderate'),
          lowActivity: t('UserAdministrationComponent.activityLevels.lowActivity', 'Low Activity'),
          inactive: t('UserAdministrationComponent.activityLevels.inactive', 'Inactive'),
        };
        const activityLevel = getActivityLevelFromCounts(generalData, noTimeTranslations);

        const summary = {
          formattedTotal: `${loginEventsCountValue} ${t('UserActivityChartComponent.summaryCards.loginEvents', 'login events')}`,
          activeDays: kpiRecordsCountValue > 0 ? `${kpiRecordsCountValue} ${t('UserActivityChartComponent.summaryCards.kpiRecords', 'KPI records')}` : 'N/A',
          activityLevel,
          hasNoTimeData: true, // Flag to show hint message
        };

        return summary;
      }

      const translations = {
        veryActive: this.$t('UserAdministrationComponent.activityLevels.veryActive'),
        active: this.$t('UserAdministrationComponent.activityLevels.active'),
        moderate: this.$t('UserAdministrationComponent.activityLevels.moderate'),
        lowActivity: this.$t('UserAdministrationComponent.activityLevels.lowActivity'),
        inactive: this.$t('UserAdministrationComponent.activityLevels.inactive'),
      };

      // Use period-specific activity counts if available, fall back to batch data.
      // This ensures the Activity Level card reflects the selected period.
      const periodStats = this.$store.getters['userTimeKpi/getUserActivityStats'](
        this.userId,
        this.activityStatsPeriodKey,
      );
      const activityCounts = periodStats || generalData || null;
      return getActivitySummary(data, translations, activityCounts);
    },

    /**
     * Build the store key for period-specific activity stats.
     */
    activityStatsPeriodKey() {
      if (this.selectedPeriod === 'custom' && this.customStartDate && this.customEndDate) {
        const s = new Date(this.customStartDate);
        const e = new Date(this.customEndDate);
        return `custom_${s.toISOString()}_${e.toISOString()}`;
      }
      return this.periodType || 'weekly';
    },

    /**
     * Human-readable label for the period shown in the Activity Breakdown header.
     */
    activityBreakdownPeriodLabel() {
      if (this.selectedPeriod === 'custom' && this.customStartDate && this.customEndDate) {
        const fmt = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return `${fmt(this.customStartDate)} – ${fmt(this.customEndDate)}`;
      }
      switch (this.periodType) {
        case 'daily': return 'last 24 hours';
        case 'weekly': return 'last 7 days';
        case 'monthly': return 'last month';
        default: return 'selected period';
      }
    },

    /**
     * Breakdown of individual activity indicators that determine the activity level.
     * Uses period-specific stats when available, falls back to batch API data (last 7 days).
     */
    activityBreakdown() {
      // Try period-specific data first (fetched alongside the chart data)
      const periodData = this.$store.getters['userTimeKpi/getUserActivityStats'](
        this.userId,
        this.activityStatsPeriodKey,
      );

      // Fallback: batch API data (always last 7 days, used for the table)
      const batchData = this.getUserActivityData(this.userId);

      const data = periodData || batchData;
      if (!data) return null;

      // Helper: translate with fallback
      const t = (key, fallback) => {
        const translated = this.$t(key);
        return translated !== key ? translated : fallback;
      };

      return [
        {
          key: 'logins',
          label: t('UserActivityChartComponent.activityBreakdown.loginEvents', 'Logins'),
          count: data.loginEventsCount || 0,
          icon: 'pi-sign-in',
        },
        {
          key: 'assessments',
          label: t('UserActivityChartComponent.activityBreakdown.assessments', 'Assessments'),
          count: data.submittedAssessmentsCount || 0,
          icon: 'pi-file-check',
        },
        {
          key: 'levels',
          label: t(
            'UserActivityChartComponent.activityBreakdown.levelsCompleted',
            'Levels Completed',
          ),
          count: data.levelProgressCount || 0,
          icon: 'pi-star',
        },
        {
          key: 'devtips',
          label: t('UserActivityChartComponent.activityBreakdown.devTips', 'DevTips'),
          count: data.devTipsCompletedCount || 0,
          icon: 'pi-lightbulb',
        },
        {
          key: 'travelbuddy',
          label: t('UserActivityChartComponent.activityBreakdown.travelBuddy', 'TravelBuddy'),
          count: data.travelBuddyCompletedCount || 0,
          icon: 'pi-users',
        },
      ];
    },

    hasNoTimeData() {
      // Check period-specific data first
      let chartKey = this.periodType;
      if (this.periodType === 'custom' && this.customStartDate && this.customEndDate) {
        const startDate = new Date(this.customStartDate);
        const endDate = new Date(this.customEndDate);
        chartKey = `${this.userId}_custom_${startDate.toISOString()}_${endDate.toISOString()}`;
      } else {
        chartKey = `${this.userId}_${this.periodType}`;
      }

      const periodData = this.$store.getters['userTimeKpi/getUserChartData'](this.userId, chartKey);
      const generalData = this.getUserActivityData(this.userId);

      // Check if period data has metrics (new format: sessionMetrics,
      // calculation.timeMetrics, or old: stored.metrics / metrics)
      const periodHasMetrics = (periodData?.sessionMetrics?.totalTimeSpent ?? 0) > 0
        || periodData?.calculation?.timeMetrics || periodData?.calculation?.metrics
        || periodData?.stored?.metrics || periodData?.metrics;
      // Check if general data has session metrics (new format) or metrics (old format)
      const generalHasMetrics = (generalData?.sessionMetrics?.totalTimeSpent ?? 0) > 0
        || generalData?.calculation?.timeMetrics || generalData?.calculation?.metrics
        || generalData?.stored?.metrics || generalData?.metrics;

      // If ANY data has metrics, user has time data - show normal view
      if (periodHasMetrics || generalHasMetrics) {
        return false;
      }

      // Only show "no time data" view if:
      // 1. No metrics in period data AND no metrics in general data
      // 2. AND user has login events (so we can show login activity)
      const generalHasLoginEvents = generalData?.stored?.loginEventsCount
       || generalData?.loginEventsCount;

      return !generalHasMetrics && !!generalHasLoginEvents;
    },
  },

  methods: {
    getPeriodLabel,

    formatDateRange(periodType) {
      const now = new Date();
      let startDate = new Date();

      // Handle custom date range
      if (periodType === 'custom' && this.customStartDate && this.customEndDate) {
        startDate = new Date(this.customStartDate);
        const endDate = new Date(this.customEndDate);

        const formatDate = (date) => date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });

        return `${formatDate(startDate)} - ${formatDate(endDate)}`;
      }

      // Handle predefined periods
      switch (periodType) {
        case 'daily':
          startDate.setDate(now.getDate() - 1);
          break;
        case 'weekly':
          startDate.setDate(now.getDate() - 7);
          break;
        case 'monthly':
          startDate.setMonth(now.getMonth() - 1);
          break;
        default:
          startDate.setDate(now.getDate() - 7);
      }

      const formatDate = (date) => date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });

      return `${formatDate(startDate)} - ${formatDate(now)}`;
    },

    async loadChartData() {
      try {
        // Use custom date range if provided, otherwise use period type
        const payload = {
          userId: this.userId,
          periodType: this.periodType,
          updateActivityData: false, // Don't overwrite table data when loading chart
        };

        // Determine effective start/end dates for activity stats query
        let effectiveStartDate = null;
        let effectiveEndDate = new Date();
        let effectivePeriodType = this.periodType;

        // Check if custom date range is selected and has both dates
        if (this.selectedPeriod === 'custom' && this.customStartDate && this.customEndDate) {
          payload.startDate = new Date(this.customStartDate);
          payload.endDate = new Date(this.customEndDate);
          payload.periodType = 'custom';
          effectiveStartDate = payload.startDate;
          effectiveEndDate = payload.endDate;
          effectivePeriodType = 'custom';

          await this.$store.dispatch('userTimeKpi/fetchUserActivityChart', {
            userId: this.userId,
            periodType: 'custom',
            startDate: payload.startDate,
            endDate: payload.endDate,
          });
        } else if (this.startDate && this.endDate) {
          // Fallback to props if provided
          payload.startDate = this.startDate;
          payload.endDate = this.endDate;
          payload.periodType = 'custom';
          effectiveStartDate = payload.startDate;
          effectiveEndDate = payload.endDate;
          effectivePeriodType = 'custom';

          await this.$store.dispatch('userTimeKpi/fetchUserActivityChart', {
            userId: this.userId,
            periodType: 'custom',
            startDate: payload.startDate,
            endDate: payload.endDate,
          });
        } else {
          await this.$store.dispatch('userTimeKpi/calculateAndGetUserKpi', payload);
        }

        // Also fetch activity stats (event counts) for the selected period
        // so the Activity Breakdown section reflects the correct date range.
        await this.$store.dispatch('userTimeKpi/fetchUserActivityStats', {
          userId: this.userId,
          periodType: effectivePeriodType,
          startDate: effectiveStartDate,
          endDate: effectiveEndDate,
        });
      } catch (error) {
        console.error('Error loading chart data:', error);
      }
    },

    async onPeriodChange() {
      // Clear custom date range if switching away from custom
      if (this.selectedPeriod !== 'custom') {
        this.customStartDate = null;
        this.customEndDate = null;
      }

      // Fetch new data for the selected period
      await this.loadChartData();
    },

    async onCustomDateChange() {
      // Only fetch data if both dates are selected
      if (this.customStartDate && this.customEndDate) {
        // Validate that start date is before end date
        const startDate = new Date(this.customStartDate);
        const endDate = new Date(this.customEndDate);

        if (startDate > endDate) {
          this.$toast?.add({
            severity: 'warn',
            summary: 'Invalid Date Range',
            detail: 'Start date must be before end date',
            life: 3000,
          });
          this.customEndDate = null;
          return;
        }

        // Auto-fetch when both dates are selected
        await this.loadChartData();
      }
    },

    clearCustomDateRange() {
      this.customStartDate = null;
      this.customEndDate = null;
      // Switch back to weekly if custom range is cleared
      if (this.selectedPeriod === 'custom') {
        this.selectedPeriod = 'weekly';
        this.loadChartData();
      }
    },
  },

  async mounted() {
    await this.loadChartData();
    // Wait two ticks so the Dialog's DOM is fully painted before allowing
    // the Chart.js canvas to render. Without this, PrimeVue's Chart
    // component can pass a null canvas ref to new Chart(), crashing.
    await this.$nextTick();
    await this.$nextTick();
    this.chartReady = true;
  },

  watch: {
    userId: {
      handler() {
        this.loadChartData();
      },
      immediate: false, // Avoid double request: mounted() already loads once
    },
    // Note: no periodType watcher needed - onPeriodChange() handles it directly
  },
};
</script>

<style scoped>
.user-activity-chart {
  min-height: 400px;
}

.chart-container {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
